import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

// Константы для API
const API_URL = 'https://guide.zerdegroup.kz/api/';
const ADMIN_COOKIE_KEY = 'zerde_admin_token';

// Интерфейс для пользователя
interface User {
  id: number;
  login: string;
  role: string;
  full_access: number;
  lastLogin?: string;
}

const UsersList: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [updateLoading, setUpdateLoading] = useState<number | null>(null);

  // Загрузка списка пользователей
  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const token = Cookies.get(ADMIN_COOKIE_KEY);
      
      if (!token) {
        navigate('/auth');
        return;
      }

      const response = await fetch(`${API_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка при загрузке пользователей');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке данных');
    } finally {
      setIsLoading(false);
    }
  };

  // Загрузка пользователей при монтировании компонента
  useEffect(() => {
    fetchUsers();
  }, [navigate]);

  const handleEdit = (userId: number) => {
    navigate(`/admin/panel/users/edit/${userId}`);
  };

  const handleDelete = (userId: number) => {
    setUserToDelete(userId);
    setDeleteDialogOpen(true);
    setDeleteError('');
  };

  const handleFullAccessChange = async (userId: number, currentValue: number) => {
    try {
      setUpdateLoading(userId);
      const token = Cookies.get(ADMIN_COOKIE_KEY);
      
      if (!token) {
        navigate('/auth');
        return;
      }

      const newValue = currentValue === 1 ? 0 : 1;
      const response = await fetch(`${API_URL}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ full_access: newValue }),
      });

      if (!response.ok) {
        throw new Error('Ошибка при обновлении прав доступа');
      }

      // Обновляем состояние пользователя в списке
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { ...user, full_access: newValue }
            : user
        )
      );

      setSuccessMessage('Права доступа успешно обновлены');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка при обновлении прав доступа');
    } finally {
      setUpdateLoading(null);
    }
  };

  const confirmDelete = async () => {
    if (userToDelete === null) return;
    
    try {
      setDeleteLoading(true);
      setDeleteError('');
      
      const token = Cookies.get(ADMIN_COOKIE_KEY);
      
      if (!token) {
        navigate('/auth');
        return;
      }

      const response = await fetch(`${API_URL}/users/${userToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status !== 204) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Ошибка при удалении пользователя');
        }
      }

      // Обновляем список пользователей
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userToDelete));
      setSuccessMessage('Пользователь успешно удален');
      
      // Скрываем сообщение об успехе через 3 секунды
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Произошла ошибка при удалении пользователя');
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Администратор';
      case 'editor':
        return 'Редактор';
      default:
        return role;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Пользователи
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/admin/panel/users/create')}
        >
          Создать пользователя
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Логин</TableCell>
                <TableCell>Роль</TableCell>
                <TableCell>Полный доступ</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length > 0 ? (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.login}</TableCell>
                    <TableCell>{getRoleLabel(user.role)}</TableCell>
                    <TableCell>
                      <Checkbox
                        checked={user.full_access === 1}
                        onChange={() => handleFullAccessChange(user.id, user.full_access)}
                        disabled={updateLoading === user.id}
                      />
                      {updateLoading === user.id && (
                        <CircularProgress size={20} sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => handleEdit(user.id)}
                        color="primary"
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(user.id)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Нет данных о пользователях
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить этого пользователя?
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteLoading}
          >
            Отмена
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} color="inherit" /> : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersList;